import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, Database, Cpu, Target, CheckCircle, Star, ArrowRight, Phone, Calendar, MapPin, Users, Award, BookOpen, TrendingUp, MessageCircle, Play, Trophy, Shield, Zap, ChevronRight, Quote, Sparkles, Rocket, Globe, Building2, GraduationCap, Briefcase, DollarSign, Heart, Eye, MousePointer, BarChart3, Lightbulb, Layers, GitBranch, Server, Monitor, Smartphone, Tablet, Wifi, Lock, Unlock, RefreshCw, Maximize2, Minimize2, RotateCcw, RotateCw, ZoomIn, ZoomOut, Move, Copy, Scissors, FileText, Image, Video, Music, File, Folder, HardDrive, Cloud, Download, Upload, Share2, Mail, Bell, Settings, Search, Filter, Grid, List, Menu, X, Plus, Minus, Edit, Trash2, Save, Brain, Activity, PieChart, Clock, User, Brain as BrainIcon
} from 'lucide-react';
import CountdownTimer from '../components/CountdownTimer';

const testimonials = [
  {
    name: "Rahul Kumar",
    role: "Software Engineer",
    quote: "Dr. Dharmendra Bajpai's DSA masterclass transformed my problem-solving skills. The C programming foundation was rock solid!",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  {
    name: "Priya Sharma",
    role: "Computer Science Student",
    quote: "The systematic approach to DSA and hands-on coding practice made complex concepts crystal clear. Highly recommended!",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg"
  }
];

const alumni = [
  {
    name: "Amit Patel",
    preRole: "B.Tech Student",
    postRole: "Software Developer @ Infosys",
    avatar: "https://randomuser.me/api/portraits/men/45.jpg"
  },
  {
    name: "Neha Singh",
    preRole: "MCA Student",
    postRole: "Jr. Developer @ TCS",
    avatar: "https://randomuser.me/api/portraits/women/65.jpg"
  }
];

const upcomingEvents = [
  {
    title: "Advanced Data Structures Workshop",
    date: "August 15, 2024",
    time: "6:00 PM - 8:00 PM",
    instructor: "Dr. Dharmendra Bajpai"
  }
];

const topics = [
  { icon: <Code className="w-6 h-6" />, title: "C Programming Fundamentals" },
  { icon: <Database className="w-6 h-6" />, title: "Data Structures (Arrays, Linked Lists, Trees)" },
  { icon: <Cpu className="w-6 h-6" />, title: "Algorithms & Problem Solving" },
  { icon: <Activity className="w-6 h-6" />, title: "Time & Space Complexity Analysis" },
  { icon: <BarChart3 className="w-6 h-6" />, title: "Sorting & Searching Algorithms" },
  { icon: <Lightbulb className="w-6 h-6" />, title: "Dynamic Programming" },
  { icon: <Target className="w-6 h-6" />, title: "Interview Preparation & Mock Tests" },
];

const MasterclassFoundationDsaC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleRegister = (e) => {
    e.preventDefault();
    setIsFormSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0e1129] dark:from-[#0a0f1c] dark:via-[#1a1a2e] dark:to-[#0e1129] relative">
      {/* Hero Section - Advanced */}
      <section className="relative flex items-center justify-center min-h-[calc(100vh-96px)] pt-20 pb-8 px-4 mt-12 overflow-hidden">
        {/* Animated SVG Code Background */}
        <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 1440 800" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <radialGradient id="bg1" cx="50%" cy="50%" r="80%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#1e293b" stopOpacity="0.0" />
            </radialGradient>
            <linearGradient id="neon" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#06b6d4" />
            </linearGradient>
          </defs>
          <rect width="1440" height="800" fill="url(#bg1)" />
          {/* Code structure lines */}
          <g stroke="url(#neon)" strokeWidth="2" opacity="0.18">
            <polyline points="100,200 300,100 500,300 700,200 900,400 1100,250 1300,350" />
            <polyline points="200,600 400,500 600,700 800,600 1000,750 1200,650 1400,700" />
            <polyline points="300,400 600,200 900,500 1200,300" />
          </g>
          {/* Code blocks */}
          <g>
            <rect x="200" y="150" width="120" height="80" rx="8" fill="#3b82f6" opacity="0.3" />
            <rect x="800" y="300" width="100" height="60" rx="6" fill="#06b6d4" opacity="0.3" />
            <rect x="1100" y="200" width="140" height="90" rx="10" fill="#3b82f6" opacity="0.3" />
            <rect x="400" y="500" width="110" height="70" rx="7" fill="#06b6d4" opacity="0.3" />
          </g>
        </svg>
        
        <div className="w-full max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative z-10">
          {/* Left: Headline, Subheadline, USPs, Mentor */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-7">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold shadow-lg"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              🆕 New Masterclass
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            >
              Foundation
              <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                DSA & C
              </span>
              Masterclass
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-300 leading-relaxed max-w-lg"
            >
              Master the fundamentals of programming with C language and Data Structures & Algorithms. Build a strong foundation for your software engineering career.
            </motion.p>

            {/* Key Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-md"
            >
              {[
                { icon: <Code className="w-5 h-5" />, text: "C Programming Fundamentals" },
                { icon: <Database className="w-5 h-5" />, text: "Data Structures & Algorithms" },
                { icon: <Target className="w-5 h-5" />, text: "Problem Solving Techniques" },
                { icon: <Trophy className="w-5 h-5" />, text: "Interview Preparation" }
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 text-gray-300">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500/20 to-cyan-500/20 flex items-center justify-center text-blue-400">
                    {feature.icon}
                  </div>
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </motion.div>

            {/* Mentor Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                DB
              </div>
              <div>
                <h3 className="font-semibold text-white">Dr. Dharmendra Bajpai</h3>
                <p className="text-sm text-gray-400">Senior Software Architect</p>
                <div className="flex items-center mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                  <span className="text-sm text-gray-400 ml-2">5.0 (200+ reviews)</span>
                </div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex flex-col sm:flex-row gap-4 w-full max-w-md"
            >
                             <button className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                 <span className="flex items-center justify-center">
                   <Play className="w-5 h-5 mr-2" />
                   Enroll Now - FREE
                 </span>
               </button>
              <button className="flex-1 bg-transparent border-2 border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white font-bold py-4 px-8 rounded-xl transition-all duration-300">
                <span className="flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Get Details
                </span>
              </button>
            </motion.div>
          </div>

          {/* Right: Course Preview Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
              {/* Course Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Code className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Foundation DSA & C</h3>
                <p className="text-gray-400">12-Week Intensive Program</p>
              </div>

              {/* Course Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="text-2xl font-bold text-blue-400">12</div>
                  <div className="text-sm text-gray-400">Weeks</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                  <div className="text-2xl font-bold text-cyan-400">50+</div>
                  <div className="text-sm text-gray-400">Projects</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="text-2xl font-bold text-blue-400">200+</div>
                  <div className="text-sm text-gray-400">Problems</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                  <div className="text-2xl font-bold text-cyan-400">24/7</div>
                  <div className="text-sm text-gray-400">Support</div>
                </div>
              </div>

                             {/* Price */}
               <div className="text-center mb-6">
                 <div className="text-3xl font-bold text-green-400 mb-2">FREE</div>
                 <div className="text-sm text-gray-400">No Cost, No Hidden Charges</div>
               </div>

                             {/* Enrollment Status */}
               <div className="mb-6">
                 <div className="text-center text-sm text-gray-400 mb-2">Enrollment Status:</div>
                 <div className="text-center">
                   <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                     <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                     Open for Enrollment
                   </span>
                 </div>
               </div>

              {/* Features */}
              <div className="space-y-3">
                {[
                  "Live Interactive Sessions",
                  "Hands-on Coding Practice",
                  "Interview Preparation",
                  "Certificate of Completion",
                  "Lifetime Access",
                  "Job Placement Support"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">What You'll Learn</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive curriculum designed to build a strong foundation in programming and problem-solving
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {topics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-blue-500/30 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white mb-4">
                  {topic.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{topic.title}</h3>
                <p className="text-gray-400">
                  Master essential concepts with hands-on practice and real-world applications
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Curriculum Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent to-blue-900/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Course Curriculum</h2>
            <p className="text-xl text-gray-400">12 weeks of intensive learning</p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                week: "Week 1-2",
                title: "C Programming Fundamentals",
                topics: ["Variables, Data Types", "Control Structures", "Functions", "Arrays", "Pointers"]
              },
              {
                week: "Week 3-4",
                title: "Basic Data Structures",
                topics: ["Arrays & Strings", "Linked Lists", "Stacks", "Queues", "Basic Operations"]
              },
              {
                week: "Week 5-6",
                title: "Advanced Data Structures",
                topics: ["Trees & Binary Trees", "Binary Search Trees", "Heaps", "Graphs", "Hash Tables"]
              },
              {
                week: "Week 7-8",
                title: "Searching & Sorting",
                topics: ["Linear & Binary Search", "Bubble Sort", "Quick Sort", "Merge Sort", "Heap Sort"]
              },
              {
                week: "Week 9-10",
                title: "Algorithm Analysis",
                topics: ["Time Complexity", "Space Complexity", "Big O Notation", "Algorithm Optimization", "Performance Analysis"]
              },
              {
                week: "Week 11-12",
                title: "Advanced Algorithms & Interview Prep",
                topics: ["Dynamic Programming", "Greedy Algorithms", "Backtracking", "Mock Interviews", "Problem Solving"]
              }
            ].map((module, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {module.week}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3">{module.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {module.topics.map((topic, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">What Students Say</h2>
            <p className="text-xl text-gray-400">Success stories from our alumni</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              >
                <div className="flex items-start space-x-4 mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <h4 className="font-semibold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-300 italic">"{testimonial.quote}"</p>
                <div className="flex items-center mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl p-12 border border-blue-500/20"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Master DSA & C?</h2>
            <p className="text-xl text-gray-400 mb-8">
              Join hundreds of students who have transformed their programming skills with Dr. Dharmendra Bajpai
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                             <button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                 <span className="flex items-center justify-center">
                   <Play className="w-5 h-5 mr-2" />
                   Enroll Now - FREE
                 </span>
               </button>
              <button className="bg-transparent border-2 border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white font-bold py-4 px-8 rounded-xl transition-all duration-300">
                <span className="flex items-center justify-center">
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MasterclassFoundationDsaC;
